import { useState, useRef, useEffect } from "react";
import { api } from "@/lib/api";
import { MapPin, Loader2 } from "lucide-react";

// Google-style AU address autocomplete (backed by /api/places proxy).
export default function AddressAutocomplete({ value, onChange, onSelect, placeholder, testid }) {
  const [q, setQ] = useState(value || "");
  const [sugs, setSugs] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [typed, setTyped] = useState(false);
  const ref = useRef(null);
  const timer = useRef();

  useEffect(() => { setQ(value || ""); }, [value]);
  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const search = (t) => {
    setQ(t); onChange(t); setOpen(true); setTyped(true);
    clearTimeout(timer.current);
    if (t.trim().length < 3) { setSugs([]); return; }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const r = await api.get(`/places/autocomplete?q=${encodeURIComponent(t)}`);
        setSugs(r.data.suggestions || []);
      } catch (e) { setSugs([]); }
      finally { setLoading(false); }
    }, 300);
  };

  const pick = async (s) => {
    setOpen(false); setQ(s.text); onChange(s.text); setSugs([]);
    try {
      const r = await api.get(`/places/details/${s.place_id}`);
      onSelect && onSelect(r.data);
    } catch (e) {}
  };

  return (
    <div className="relative mt-1" ref={ref}>
      <div className="relative">
        <MapPin className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input data-testid={testid} value={q} onChange={(e) => search(e.target.value)} onFocus={() => q.length >= 3 && setOpen(true)}
          placeholder={placeholder || "Start typing an address…"}
          className="w-full pl-9 pr-9 py-2 border border-slate-200 rounded-lg text-sm outline-none focus:border-navy" />
        {loading && <Loader2 className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 animate-spin" />}
      </div>
      {open && (sugs.length > 0 || (typed && q.trim().length >= 3 && !loading)) && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {sugs.length === 0 ? (
            <p className="text-sm text-slate-400 px-3 py-3" data-testid="address-no-match">No matching address found — you can enter it manually.</p>
          ) : sugs.map((s) => (
            <button key={s.place_id} type="button" data-testid={`address-option-${s.place_id}`} onClick={() => pick(s)}
              className="w-full flex items-start gap-2 px-3 py-2.5 text-sm text-left hover:bg-slate-50">
              <MapPin className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" /><span className="text-navy">{s.text}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
